import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ScreeningCaseCompositeEntity } from '@nk/data-entities-lib';
import {
  ButtonDirective,
  CardComponent,
  CardContentComponent,
  ErrorAlertComponent,
  ProgressComponent,
} from '@nk/ui-lib';
import { downloadFileByUrl, getPdfSrcUrl } from '@nk/utils-lib';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { catchError, map, Observable, of } from 'rxjs';
import { ScreeningCaseService } from '../../services/screening-case.service';
import { getEntityName } from '../../utils/screening-overview-card-list.utils';

@Component({
  selector: 'nk-screening-case-report-pdf',
  imports: [
    AsyncPipe,
    ErrorAlertComponent,
    PdfViewerModule,
    TranslateModule,
    ProgressComponent,
    CardComponent,
    CardContentComponent,
    ButtonDirective,
  ],
  templateUrl: './screening-case-report-pdf.component.html',
  styles: [
    `
      .pdf-view {
        display: block;
        height: 80vh;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningCaseReportPdfComponent implements OnInit {
  public screeningConfigUuid = input.required<string>();
  public screeningCaseComposite = input.required<ScreeningCaseCompositeEntity>();
  public printView = input<boolean>(false);
  public error = signal<HttpErrorResponse | null>(null);
  public src$!: Observable<string | null>;
  public isLoading = signal(false);
  private _screeningCaseService = inject(ScreeningCaseService);
  private _translateService = inject(TranslateService);

  public screeningEntityUrl = computed(() => {
    const screeningConfigUuid = this.screeningConfigUuid();
    const screeningEntityUuid = this.screeningCaseComposite()?.screening_entity?.uuid;
    const personUuid = this.screeningCaseComposite()?.person?.uuid;
    if (personUuid) {
      return `${window.location.origin}/person/${personUuid}/screening`;
    }
    const companyUuid = this.screeningCaseComposite()?.company?.uuid;
    if (companyUuid) {
      return `${window.location.origin}/company/${screeningConfigUuid}/screening/${screeningEntityUuid}/screening-cases`;
    }
    const customerPersonUuid = this.screeningCaseComposite()?.customer_person?.uuid;
    if (customerPersonUuid) {
      return `${window.location.origin}/screening/customer-person/${customerPersonUuid}/${screeningEntityUuid}/screening-cases`;
    }
    return null;
  });

  public ngOnInit(): void {
    const screeningConfigUuid = this.screeningConfigUuid();
    const screeningCaseUuid = this.screeningCaseComposite().screening_case.uuid;
    if (!screeningConfigUuid || !screeningCaseUuid) {
      return;
    }

    this.isLoading.set(true);
    const screeningCaseReport$ = this._screeningCaseService.screeningCaseReportCache
      .get(screeningConfigUuid, screeningCaseUuid)
      .pipe(
        map((data) => {
          if (!data?.pdf_base64) {
            throw new HttpErrorResponse({
              status: 404,
              statusText: `pdf_base64 is ${data?.pdf_base64}`,
            });
          }
          this.isLoading.set(false);
          return data;
        }),
        catchError((error: HttpErrorResponse) => {
          this.error.set(error);
          this.isLoading.set(false);
          return of(null);
        }),
      );

    this.src$ = screeningCaseReport$.pipe(map((data) => (data?.pdf_base64 ? getPdfSrcUrl(data.pdf_base64) : null)));
  }

  public downloadPdf(screeningCaseComposite: ScreeningCaseCompositeEntity, downloadUrl: string): void {
    if (!downloadUrl) {
      return;
    }
    const prefix = this._translateService.instant(
      extractTranslation('screening.case.review.pep_match_title'),
    ) as string;
    const entityName = getEntityName(screeningCaseComposite);
    const fileName = `${prefix} - ${entityName}`;
    downloadFileByUrl(downloadUrl, fileName);
  }
}
